import { useMemo } from 'react';
import { TemplateFlowPopoverItem, UseTemplateFlowResult } from '@frontend/integrated-messaging';
import { useFormsTeaserConfig } from '@frontend/self-upgrade';
import { ModalControlResponse } from '@frontend/design-system';
import { RenderTemplateArgs } from '../types';
import { useAllTemplatesFlow } from './use-all-template-flows';
import { ModalWrapperProps, useFormsPromotionFlow } from './use-forms-promotion-flow';
import { useFormsTemplateFlow } from './use-forms-template-flow';
import { useReviewInvitationTemplateFlow } from './use-review-invitation-template-flow';
import { useScheduleTemplateFlow } from './use-schedule-template-flow';
import { useTextToPayTemplateFlow } from './use-text-to-pay-template-flow';

interface IntegratedTemplateFlowProps {
  groupId: string;
  personId?: string;
  personPhone: string;
  renderTemplate: (args: RenderTemplateArgs) => void;
  modalControl: ModalControlResponse;
}

interface IntegratedTemplateFlowsResult {
  popoverItems: TemplateFlowPopoverItem[];
  templateTypeProps: Record<string, UseTemplateFlowResult>;
  formPromotionModalProps: ModalWrapperProps;
}

export const useIntegratedTemplateFlows = ({
  groupId,
  personId,
  personPhone,
  renderTemplate,
  modalControl,
}: IntegratedTemplateFlowProps): IntegratedTemplateFlowsResult => {
  const {
    actionLabel: formsPromotionTooltipText,
    showFormsTeaser,
    isFormsActive,
    trackingId: formsPromotionTrackingId,
  } = useFormsTeaserConfig({
    locationIds: [groupId],
    source: 'inbox-thread-template',
  });

  const schedule = useScheduleTemplateFlow({
    groupId,
    personId,
    renderTemplate,
    modalControl,
  });

  const review = useReviewInvitationTemplateFlow({
    groupId,
    personId,
    renderTemplate,
    modalControl,
  });

  const textToPay = useTextToPayTemplateFlow({
    groupId,
    personId,
    renderTemplate,
    modalControl,
  });

  const all = useAllTemplatesFlow({
    groupId,
    personId,
    renderTemplate,
    modalControl,
  });

  const formsPromotion = useFormsPromotionFlow({
    tooltipText: formsPromotionTooltipText,
    trackingId: formsPromotionTrackingId,
  });

  const formsTemplate = useFormsTemplateFlow({
    disabled: !isFormsActive,
    groupId,
    personId,
    personPhone,
    renderTemplate,
    modalControl,
    showFormsTeaser,
  });

  const forms = showFormsTeaser ? formsPromotion : formsTemplate;

  const isPopoverItem = (item: TemplateFlowPopoverItem | undefined): item is TemplateFlowPopoverItem => Boolean(item);

  return useMemo(
    () => ({
      popoverItems: [
        schedule.popoverItem,
        review.popoverItem,
        forms.popoverItem,
        textToPay.popoverItem,
        all.popoverItem,
      ].filter(isPopoverItem),

      templateTypeProps: {
        allTemplateTypeProps: all,
        scheduleTemplateTypeProps: schedule,
        reviewInvitationTypeProps: review,
        selectInvoiceTypeProps: textToPay,
        formsTemplateTypeProps: formsTemplate,
      },

      formPromotionModalProps: formsPromotion.modalProps,
    }),
    [schedule, review, forms, textToPay, all, formsTemplate, formsPromotion.modalProps]
  );
};
